import { motion, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { type FC, useEffect } from "react";

/** Default spring tuning — a gentle count-up that settles without overshoot. */
const DEFAULT_SPRING = { stiffness: 100, damping: 30 } as const;

export type AnimatedNumberProps = {
	/** The target number. Changes spring from the current display value → this. */
	value: number;
	/**
	 * Spring tuning for the count animation. Higher stiffness = snappier; higher damping =
	 * less overshoot. Defaults to a gentle 100/30. Ignored under reduced motion (jumps).
	 */
	springConfig?: { stiffness: number; damping: number };
	/**
	 * Play the 0 → value count-up when it first mounts (default `true`). Pass `false` for a figure that has
	 * to be exact the moment it appears — only later changes then animate, from the old value to the new.
	 */
	animateOnMount?: boolean;
	dataTestId?: string;
};

/**
 * A number that springs from its current value to `value` (a count-up/down), rendered as a
 * `motion.span` so it can live INSIDE a styled `Typography`/element and inherit its
 * color/weight/tabular-nums. Reuse for any focal metric that should animate in sync with a
 * nearby instrument (a score, a total, a projected value).
 *
 * Honors `prefers-reduced-motion`: when set, it jumps straight to `value` with no count.
 */
export const AnimatedNumber: FC<AnimatedNumberProps> = ({ value, springConfig = DEFAULT_SPRING, animateOnMount = true, dataTestId }) => {
	const reduced = useReducedMotion();
	// Seed at the value under reduced motion, or when the caller opts out of the entrance, so the first
	// paint is already correct (no flash); otherwise seed at 0 so the mount plays the count-up entrance.
	const spring = useSpring(reduced || !animateOnMount ? value : 0, springConfig);
	const display = useTransform(spring, (current) => Math.round(current));

	useEffect(() => {
		if (reduced) spring.jump(value);
		else spring.set(value);
	}, [spring, value, reduced]);

	return <motion.span data-testid={dataTestId}>{display}</motion.span>;
};
